import { call, put, select, takeLatest, all } from 'redux-saga/effects';
import { BcfImportActions, BcfImportActionKeys } from './BcfImport.actions';
import { BcfImportApi } from 'Data/BcfImport.api';
import { BcfImportStep } from './interfaces/IBcfImportState';
import { currentStepSelector, mappingConfigsSelector, selectedFileSelector, importSettingsSelector } from './BcfImport.selector';

export function* analyzeFileSaga(action: any): any {
  try {
    const response = yield call(BcfImportApi.analyzeBcfFile, action.payload);
    // API returns { success, data, info } - extract the actual data
    yield put(BcfImportActions.analyzeFileSuccess(response.data.data));
  } catch (error: any) {
    yield put(BcfImportActions.analyzeFileFailure(error?.message || 'Falha ao analisar arquivo BCF'));
  }
}

export function* fetchMappingDefaultsSaga(action: any): any {
  try {
    const constructionSiteId = action.payload;
    const response = yield call(BcfImportApi.fetchDefaults, constructionSiteId);
    // API returns { success, data, info } - extract the actual data
    yield put(BcfImportActions.fetchMappingDefaultsSuccess(response.data.data));
  } catch (error: any) {
    yield put(BcfImportActions.fetchMappingDefaultsFailure(error?.message || 'Falha ao buscar valores padrão'));
  }
}

export function* validateAndAdvanceSaga(action: any): any {
  try {
    const currentStep = action.payload;
    const state = yield select();

    // Step-specific validation
    switch (currentStep) {
      case BcfImportStep.FILE_UPLOAD: {
        const fileAnalysisResult = yield select((s: any) => s.bcfImport.fileAnalysisResult);
        if (!fileAnalysisResult) {
          yield put(BcfImportActions.setError('Por favor, analise um arquivo BCF primeiro'));
          return;
        }
        break;
      }

      case BcfImportStep.DATA_MAPPING: {
        const mappingConfigs = yield select(mappingConfigsSelector);
        // Only validate fields that have BCF values to map AND are not ignored
        const fieldsWithValues = Object.entries(mappingConfigs).filter(
          ([key, config]: [string, any]) => config.bcfValues && config.bcfValues.length > 0 && !config.isIgnored
        );

        // Check if all BCF values in each field are mapped
        const allFieldsValid = fieldsWithValues.every(([key, config]: [string, any]) => {
          const mappedCount = Object.keys(config.mappingRules || {}).length;
          return mappedCount >= config.bcfValues.length;
        });

        if (!allFieldsValid && fieldsWithValues.length > 0) {
          yield put(BcfImportActions.setError('Por favor, complete todos os mapeamentos de campos'));
          return;
        }
        break;
      }

      case BcfImportStep.SETTINGS: {
        const settings = yield select(importSettingsSelector);
        if (!settings.skipUnmappedItems && !settings.defaultAssignee) {
          yield put(BcfImportActions.setError('Defina um responsável padrão ou ative a opção de pular itens não mapeados'));
          return;
        }
        break;
      }

      case BcfImportStep.SUMMARY: {
        // Summary step just shows the client-side summary, no validation needed
        break;
      }
    }

    // Validation passed, advance to next step
    const nextStep = currentStep + 1;
    yield put(BcfImportActions.setCurrentStep(nextStep));
  } catch (error: any) {
    yield put(BcfImportActions.setError(error?.message || 'Falha na validação'));
  }
}



export function* executeImportSaga(action: any): any {
  try {
    const fileAnalysisResult = yield select((s: any) => s.bcfImport.fileAnalysisResult);
    const mappingConfigs = yield select(mappingConfigsSelector);
    const importSettings = yield select(importSettingsSelector);
    const constructionSiteId = action.payload;

    if (!fileAnalysisResult?.fileId) {
      yield put(BcfImportActions.setError('Nenhum arquivo analisado'));
      return;
    }

    // Simulate progress updates
    yield put(BcfImportActions.updateImportProgress(10));

    const response = yield call(BcfImportApi.executeImport, {
      fileId: fileAnalysisResult.fileId,
      mappingConfigs,
      importSettings,
      constructionSiteId,
    });

    yield put(BcfImportActions.updateImportProgress(100));
    // API returns { success, data, info } - extract the actual data
    yield put(BcfImportActions.executeImportSuccess(response.data.data));
  } catch (error: any) {
    yield put(BcfImportActions.executeImportFailure(error?.message || 'Falha na importação'));
  }
}

export function* bcfImportSaga(): any {
  yield all([
    takeLatest(BcfImportActionKeys.ANALYZE_BCF_FILE_REQUEST, analyzeFileSaga),
    takeLatest(BcfImportActionKeys.FETCH_MAPPING_DEFAULTS_REQUEST, fetchMappingDefaultsSaga),
    takeLatest(BcfImportActionKeys.VALIDATE_AND_ADVANCE, validateAndAdvanceSaga),
    takeLatest(BcfImportActionKeys.EXECUTE_IMPORT_REQUEST, executeImportSaga),
  ]);
}

export default bcfImportSaga;
